import React, { useState, useEffect } from 'react';
import { useNotification } from '../../contexts/NotificationContext';
import { useTutorial } from '../../contexts/TutorialContext';
import { getApiUrl } from '../../utils/api';
import useInlineFillHeight from '../../hooks/useInlineFillHeight';
import './NewTestCycleModal.css';

const DOMAIN_OS = {
  'Front-end': ['Windows', 'Linux', 'MacOS'],
  'Back-end': ['Windows', 'Linux', 'MacOS'],
  'Mobile': ['iOS', 'Android'],
  'Desktop': ['Windows', 'Linux', 'MacOS'],
};

const NewTestCycleModal = ({ isOpen, onClose, onSuccess, projectId = '', selectedFolder = null }) => {
  const { showError, showSuccess } = useNotification();
  const tutorial = useTutorial();
  const [inlineRef, inlineHeight] = useInlineFillHeight(isOpen);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    preconditions: '',
    jira_story_key: '',
    domain: '',
    operating_system: '',
    device_version: '',
    configuration: '',
    test_environment: '',
    strategy: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [effectiveProjectId, setEffectiveProjectId] = useState(projectId || '');


  // Carregar dados necessários
  useEffect(() => {
    if (isOpen) {
      setEffectiveProjectId(projectId || '');
    }
  }, [isOpen, projectId]);

  // Modal simplificado: sem recarregamentos

  // (removido)

  // (removido)

  // (removido)

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      // resetar SO quando domínio muda
      ...(name === 'domain' ? { operating_system: '' } : {})
    }));
  };

  // (removido)

  // (removido)

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      showError('Nome do ciclo é obrigatório');
      return;
    }

    if (!effectiveProjectId) {
      showError('Selecione um projeto para o ciclo');
      return;
    }
    // Jira Story Key opcional: não bloquear criação quando vazio
    // Fluxo simplificado, sem datas e sem seleção de casos

    if (tutorial?.isTutorialMode) {
      const nowIso = new Date().toISOString();
      let userName = 'Tutorial';
      let userId = null;
      try {
        const raw = localStorage.getItem('user');
        const parsed = raw ? JSON.parse(raw) : null;
        if (parsed?.name) userName = parsed.name;
        if (parsed?.id != null) userId = parsed.id;
      } catch (_) {}

      const createdCycleId = `tut-cycle-${Date.now()}`;
      const created = {
        id: createdCycleId,
        name: formData.name,
        description: formData.description,
        preconditions: formData.preconditions,
        jira_story_key: formData.jira_story_key,
        project_id: effectiveProjectId,
        folder_id: selectedFolder?.id || null,
        created_at: nowIso,
        created_by: userId,
        created_by_name: userName,
        status: 'planned',
        has_execution: false,
      };

      tutorial?.addSimulatedTestCycle?.(effectiveProjectId, created);

      let casesToLink = tutorial?.getSimulatedTestCases?.(effectiveProjectId) || [];
      if (!Array.isArray(casesToLink) || casesToLink.length === 0) {
        const fallbackCase = {
          id: `tut-tc-${Date.now()}`,
          title: 'Caso de teste (Tutorial)',
          description: 'Caso simulado para o tutorial',
          preconditions: '',
          project_id: effectiveProjectId,
          folder_id: selectedFolder?.id || null,
          created_at: nowIso,
          created_by: userId,
          created_by_name: userName,
          status: 'draft',
          priority: 'medium',
        };
        tutorial?.addSimulatedTestCase?.(effectiveProjectId, fallbackCase);
        casesToLink = [fallbackCase];
      }

      tutorial?.setSimulatedCycleCases?.(createdCycleId, casesToLink);
      showSuccess('Ciclo de teste criado com sucesso!');
      if (typeof onSuccess === 'function') onSuccess(created);
      handleClose();
      return;
    }

    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const payload = {
        name: formData.name,
        description: formData.description,
        project_id: effectiveProjectId,
        status: 'Em elaboração',
        preconditions: formData.preconditions,
        folder_id: selectedFolder?.id || null,
        jira_story_key: formData.jira_story_key,
        domain: formData.domain || null,
        operating_system: formData.operating_system || null,
        device_version: formData.device_version || null,
        configuration: formData.configuration || null,
        test_environment: formData.test_environment || null,
        strategy: formData.strategy || null
      };

      const response = await fetch(getApiUrl('test-repository/test-cycles'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();
        showSuccess('Ciclo de teste criado com sucesso!');
        const created = data?.data || data;
        onSuccess(created);
        handleClose();
      } else {
        const errorData = await response.json();
      showError(errorData.error || errorData.message || 'Erro ao criar ciclo de teste');
      }
    } catch (error) {
      console.error('Erro ao criar ciclo de teste:', error);
      showError('Erro ao criar ciclo de teste');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      preconditions: '',
      jira_story_key: '',
      domain: '',
      operating_system: '',
      device_version: '',
      configuration: '',
      test_environment: '',
      strategy: ''
    });
    setEffectiveProjectId(projectId || '');
    onClose();
  };



  if (!isOpen) return null;

  return (
    <div className="new-test-cycle-modal new-test-cycle-inline" ref={inlineRef} style={inlineHeight ? { height: inlineHeight } : undefined}>
      <div className="modal-header">
        <h2>Novo ciclo</h2>
      </div>

        <form onSubmit={handleSubmit} className="modal-form" data-tour="modal-ciclo-campos">
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="name">Nome do Ciclo de Teste *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Digite o nome do ciclo"
                required
                data-tour="modal-ciclo-nome"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="description">Descrição</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Descreva o objetivo do ciclo de teste"
                rows="3"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="preconditions">Pré Condição</label>
              <input
                type="text"
                id="preconditions"
                name="preconditions"
                value={formData.preconditions}
                onChange={handleInputChange}
                placeholder="Opcional: pré-condições para execução"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="jira_story_key">Jira Story Key</label>
              <input
                type="text"
                id="jira_story_key"
                name="jira_story_key"
                value={formData.jira_story_key}
                onChange={handleInputChange}
                placeholder="Insira o link da história"
                
              />
            </div>
          </div>



          <div className="form-section" style={{ marginTop: 8 }}>
            <div className="section-header" style={{ marginBottom: 8 }}>
              <span style={{ fontWeight: 600, fontSize: 14 }}>Plataforma</span>
            </div>

            <div className="form-row">
              <div className="form-group" style={{ flex: 1 }}>
                <label>Domínio</label>
                <select name="domain" value={formData.domain} onChange={handleInputChange}>
                  <option value="">Selecione</option>
                  {Object.keys(DOMAIN_OS).map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label>Sistema Operacional</label>
                <select name="operating_system" value={formData.operating_system} onChange={handleInputChange} disabled={!formData.domain}>
                  <option value="">Selecione</option>
                  {(DOMAIN_OS[formData.domain] || []).map(os => <option key={os} value={os}>{os}</option>)}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Device / Versão</label>
                <input type="text" name="device_version" value={formData.device_version} onChange={handleInputChange} placeholder="Ex: iPhone 17 Pro, Windows 11..." />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group" style={{ flex: 1 }}>
                <label>Configuração</label>
                <select name="configuration" value={formData.configuration} onChange={handleInputChange}>
                  <option value="">Selecione</option>
                  {['Web', 'Web Mobile', 'App', 'API'].map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label>Ambiente</label>
                <select name="test_environment" value={formData.test_environment} onChange={handleInputChange}>
                  <option value="">Selecione</option>
                  {['Dev', 'Testes', 'Homolog', 'Pré-produção', 'Produção'].map(e => <option key={e} value={e}>{e}</option>)}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Estratégia</label>
                <select name="strategy" value={formData.strategy} onChange={handleInputChange}>
                  <option value="">Selecione</option>
                  {['Funcional', 'Integração', 'Performance', 'Automatizado', 'Aceitação', 'API'].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn btn-danger" onClick={handleClose}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading} data-tour="modal-ciclo-salvar">
              {loading ? 'Salvando...' : 'Salvar e Fechar'}
            </button>
          </div>
        </form>
    </div>
  );
};

export default NewTestCycleModal;
